import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingIndicator } from '../LoadingIndicator';
import { EditBlockForm, TemplateForm } from './EditBlockForm';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import { useAddTemplateToBlock, useGetBlock, useRemoveTemplateFromBlock } from '../../hooks/api/use-blocks.hook';
import { BlockApiModel, BlockRelationship, WithTemplates } from '../../../../types/models/block.model';

export const EditBlock = () => {
  const navigate = useNavigate();
  const { blockId } = useParams<{ blockId: string }>();

  const {
    block,
    loading: loadingBlock,
    error: gettingError,
  } = useGetBlock<BlockApiModel & WithTemplates>({
    blockId: Number(blockId),
    include: [BlockRelationship.TEMPLATES],
  });
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});
  const [addTemplateToBlock, { error: addTemplateError }] = useAddTemplateToBlock();
  const [removeTemplateFromBlock, { error: removeTemplateError }] = useRemoveTemplateFromBlock();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditBlockForm>({
    resolver: classValidatorResolver(EditBlockForm),
    values: block,
  });

  const onSubmit: SubmitHandler<EditBlockForm> = async ({ templates }) => {
    if (!block) return;
    const templatesToAdd = templates.filter(({ id }) => !block.templates.find((template) => template.id === id));
    const templatesToRemove = block.templates.filter(({ id }) => !templates.find((template) => template.id === id));

    await Promise.all([
      ...templatesToAdd.map(({ id: templateId }) => addTemplateToBlock({ blockId: block.id, templateId })),
      ...templatesToRemove.map(({ id: templateId }) => removeTemplateFromBlock({ blockId: block.id, templateId })),
    ]);

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <div>
        <Controller
          control={control}
          name="templates"
          render={({ field }) => (
            <Autocomplete
              {...register('templates')}
              loading={loadingTemplates}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange([...selectedOptions])}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(template) => (template as TemplateForm)?.name ?? ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              options={templatesResponse?.pages.flatMap(({ result }) => result) ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template(s)"
                  error={Boolean(errors.templates)}
                  helperText={errors.templates?.message}
                />
              )}
            />
          )}
        />
      </div>
    );
  };

  if (loadingBlock) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingBlock ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
          <DialogTitle>Edit Block</DialogTitle>
          <DialogContent>
            {renderContent()}
            {addTemplateError || removeTemplateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {addTemplateError?.response?.data?.message ?? removeTemplateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
